import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const MicrosoftCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        setProcessing(false);
        return;
      }

      if (!code || !state) {
        setError('Missing authorization code or state');
        setProcessing(false);
        return;
      }

      try {
        // Exchange code for tokens
        const response = await api.get(`/api/auth/microsoft/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`);
        
        // Store token as backup
        if (response.data.access_token) {
          localStorage.setItem('session_token', response.data.access_token);
        }
        
        // Refresh auth state
        await checkAuth();
        
        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to complete Microsoft login';
        setError(msg);
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, checkAuth]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Login Failed</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-lg text-slate-600">Completing Microsoft login...</p>
      </div>
    </div>
  );
};

export default MicrosoftCallback;
